import React, { useEffect, useState } from "react";
import {
  Modal, Input, Button, Form, Radio,
} from "antd";
import "./PendingTestsModal.css";

const { TextArea } = Input;

const PendingTestsModal = ({
  open,
  onCancel,
  tests = [],
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && tests.length > 0) {
      // Initialize form with test data
      const initialValues = {
        tests: tests.map((test) => ({
          ...test,
          hasResult: "No",
        })),
      };
      form.setFieldsValue(initialValues);
    } else if (open && tests.length === 0) {
      form.resetFields();
    }
  }, [open, tests, form]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      if (onSubmit) {
        await onSubmit(values.tests.map((test) => {
          const newTest = { ...test };
          if (newTest.hasResult === "No") {
            newTest.Result = "";
          }
          delete newTest.hasResult;
          return newTest;
        }));
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Modal
      centered
      title="Pending Tests Require Results"
      width={800}
      open={open}
      onCancel={handleCancel}
      rootClassName="PendingTestsModal"
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Submit and Create Retest
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        className="PendingTestsModal__Form"
        requiredMark={false}
      >
        <Form.List name="tests">
          {(fields) => (
            <div className="PendingTestsModal__Content">
              {fields.map((field, index) => {
                const test = tests[index] || {};
                return (
                  <div key={field.key} className="PendingTestsModal__Card">
                    <div className="PendingTestsModal__CardHeader">
                      <div className="PendingTestsModal__IdSection">
                        <div className="PendingTestsModal__TestType">
                          <span className="PendingTestsModal__Label">Test:</span>
                          <span className="PendingTestsModal__Value">{test.Analysis}</span>
                        </div>
                        <div className="PendingTestsModal__IdItem">
                          <span className="PendingTestsModal__Label">Genista ID:</span>
                          <span className="PendingTestsModal__Value">{test.GenistaID}</span>
                        </div>
                        <div className="PendingTestsModal__IdItem">
                          <span className="PendingTestsModal__Label">Workorder:</span>
                          <span className="PendingTestsModal__Value">{test.OrderID}</span>
                        </div>
                      </div>
                    </div>

                    <div className="PendingTestsModal__FormSection">
                      <div className="PendingTestsModal__RadioContainer">
                        <span className="PendingTestsModal__RadioLabel">Do you have a result?</span>
                        <span className="PendingTestsModal__RadioIds">
                          (GB ID:
                          {" "}
                          {test.GenistaID}
                          {" | "}
                          WO ID:
                          {" "}
                          {test.OrderID}
                          )
                        </span>
                        <Form.Item
                          name={[field.name, "hasResult"]}
                          className="PendingTestsModal__RadioField"
                          initialValue="No"
                        >
                          <Radio.Group onChange={(e) => {
                            if (e.target.value === "Yes") {
                              form.validateFields([
                                ["tests", field.name, "comment"],
                              ]);
                            }
                          }}
                          >
                            <Radio value="No">No</Radio>
                            <Radio value="Yes">Yes</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </div>

                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => {
                          const prevHasResult = prevValues.tests[field.name].hasResult;
                          const currentHasResult = currentValues.tests[field.name].hasResult;
                          return prevHasResult !== currentHasResult;
                        }}
                      >
                        {({ getFieldValue }) => {
                          const hasResult = getFieldValue(["tests", field.name, "hasResult"]);
                          const showResult = hasResult === "Yes";

                          return (
                            <>
                              {showResult && (
                                <Form.Item
                                  name={[field.name, "Result"]}
                                  label="Result"
                                  className="PendingTestsModal__Field"
                                  requiredMark={false}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Result is required",
                                    },
                                  ]}
                                >
                                  <Input
                                    placeholder="Enter result"
                                    className="PendingTestsModal__Input"
                                    maxLength={300}
                                  />
                                </Form.Item>
                              )}

                              <Form.Item
                                name={[field.name, "comment"]}
                                label={hasResult === "No" ? "Comment" : "Comment (Optional)"}
                                className="PendingTestsModal__Field"
                                dependencies={[[field.name, "hasResult"]]}
                                rules={[
                                  ({ getFieldValue: getValue }) => ({
                                    validator(_, value) {
                                      const hasResultValue = getValue(["tests", field.name, "hasResult"]);
                                      if (hasResultValue === "No") {
                                        if (!value || !value.trim()) {
                                          return Promise.reject(
                                            new Error("Comment is required when there is no result"),
                                          );
                                        }
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                              >
                                <TextArea
                                  placeholder="Enter comment"
                                  autoSize={{ minRows: 3, maxRows: 3 }}
                                  className="PendingTestsModal__TextArea"
                                  maxLength={2000}
                                />
                              </Form.Item>
                            </>
                          );
                        }}
                      </Form.Item>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default PendingTestsModal;
